import { useState } from 'react';

import FavoriteIcon from '@mui/icons-material/Favorite';
import { PopperPlacementType, Skeleton, Stack } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { FacebookShareButton, EmailShareButton, TwitterShareButton } from 'react-share';
import courseAPI from 'src/api/course';
import userAPI, { USER_END_POINT } from 'src/api/user';
import config from 'src/config';
import { SocialShareType } from 'src/helpers/constants';
import { useProfile, useRequest } from 'src/helpers/use';
import { getCourseUrl } from 'src/helpers/utils';
import { ICourse, IWishlist } from 'src/interfaces';
import { useSWRConfig } from 'swr';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import { IconButtonStyle, LightTooltip, SocialBox } from '@components/atoms/iconButton/style';

type Props = {
  active: boolean;
  course: ICourse;
};

/**
 * @deprecated using FavoriteCourseIconButton instead ('@components/share/custom/actionIcon/favoriteCourseIconButton')
 */
export const FavoriteIconButton = ({ active, course }: Props) => {
  const { mutate } = useSWRConfig();
  const { profile, isLoading } = useProfile();
  const { isLoading: wishlistLoading, isValidating } = useRequest<IWishlist>(
    profile ? USER_END_POINT.USER_ME_WISHLIST_COURSES : null,
    () => userAPI.fetchUserCourseWishlist(course!.id),
    { revalidateIfStale: false },
  );

  if (isLoading || (wishlistLoading && isValidating)) {
    return (
      <IconButtonStyle>
        <Skeleton variant="circular" width={30} height={30} />
      </IconButtonStyle>
    );
  }
  if (!profile) {
    return null;
  }

  const updateWishlist = async () => {
    if (course.wishlist) {
      mutate(USER_END_POINT.USER_ME_WISHLIST_COURSES, { data: null }, false);
      await userAPI.deleteUserWishlist(course.id);
    } else {
      mutate(
        USER_END_POINT.USER_ME_WISHLIST_COURSES,
        { data: { ...course, wishlist: { course_id: course.id } } },
        false,
      );
      await userAPI.createUserWishlist({ courseId: course.id });
    }
    mutate(USER_END_POINT.USER_ME_WISHLIST_COURSES);
  };

  return (
    <IconButtonStyle onClick={updateWishlist}>
      <FavoriteIcon className="icon" sx={{ fill: active ? '#F44336' : 'transparent' }} />
    </IconButtonStyle>
  );
};

type ShareSocialProps = {
  course: ICourse;
  placement?: PopperPlacementType;
};

/**
 * @deprecated using ShareCourseIconButton instead ('@components/share/custom/actionIcon/shareCourseIconButton')
 */
export const ShareIconButton = ({ course, placement = 'bottom' }: ShareSocialProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { profile } = useProfile();

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltip = () => {
    setOpen(!open);
  };
  const url = `${config.APP_DOMAIN}${getCourseUrl(course)}`;

  const getEmailBody = () => {
    if (course.mail_use_template) {
      return `${course.mail_content}\n\r${t('check_it_out_your_self')} ${url} ${
        profile ? `\n\rBest,\n\r${profile.firstname}` : ''
      }`;
    }
    return course.mail_content;
  };

  const updateShareCounter = (type: SocialShareType) => {
    courseAPI.updateShareCounter(course.id, type);
  };

  return (
    <LightTooltip
      PopperProps={{
        disablePortal: true,
      }}
      placement={placement}
      onClose={handleTooltipClose}
      open={open}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      title={
        <Stack direction="row" sx={{ height: '40px', background: 'white' }}>
          <FacebookShareButton url={url}>
            <IconButtonStyle onClick={() => updateShareCounter(SocialShareType.Facebook)}>
              {/* <FontAwesomeIcon className="icon social" icon={faFacebookF} /> */}
              <FontAwesomeIcon className="icon social" icon="fa-brands fa-facebook-f" />
            </IconButtonStyle>
          </FacebookShareButton>
          <SocialBox />
          <TwitterShareButton url={url} title={course.tw_content}>
            <IconButtonStyle onClick={() => updateShareCounter(SocialShareType.Twitter)}>
              {/* <FontAwesomeIcon className="icon social" icon={faTwitter} /> */}
              <FontAwesomeIcon className="icon social" icon="fa-brands fa-twitter" />
            </IconButtonStyle>
          </TwitterShareButton>
          <SocialBox />
          <EmailShareButton body={getEmailBody()} url="" subject={course.mail_subject}>
            <IconButtonStyle onClick={() => updateShareCounter(SocialShareType.Email)}>
              {/* <FontAwesomeIcon className="icon social" icon={faEnvelope} /> */}
              <FontAwesomeIcon className="icon social" icon="fa-solid fa-envelope" />
            </IconButtonStyle>
          </EmailShareButton>
        </Stack>
      }
      arrow
    >
      <IconButtonStyle onClick={handleTooltip}>
        {/* <FontAwesomeIcon className="icon share" icon={faShareAlt} style={{ color: 'white' }} /> */}
        <FontAwesomeIcon className="icon share" icon="fa-light fa-share" style={{ color: 'white' }} />
      </IconButtonStyle>
    </LightTooltip>
  );
};
